import { useCallback, useMemo, useRef, useState } from "react";
import Header, { HeaderVariant } from "./Header";
import { useHeaderWithProgress } from "./HeaderWithProgress";
import styled from "styled-components";
import {
  combineMenuItems,
  createAboutLinkItem,
  useAboutLinkItem,
  useFaqLinkItem,
  useFlowBecomeMemberMenuItem,
} from "./HeaderObjects";
import { useAuthHeaderElements } from "./AuthHeaderElements";
import ContinueLaterModal from "./continueLater/ContinueLaterModal";
import { EVENTS } from "../../Tracking";
import { MenuListItem } from "@teamscheme/header-elements";
import PreviewModal from "./preview/PreviewModal";
import EyeIcon from "@material-ui/icons/VisibilityOutlined";
import StarIcon from "@material-ui/icons/Star";
import StaticHeader from "./StaticHeader";
import { useContract, useRole } from "../../contract/ContractContext";
import { ContractType } from "../../contract/model/ContractViewModel";
import UpgradeContractModal from "../../contract/contract-type/modal/UpgradeContractModal";
import { TYPE_NAMES } from "../../contract/contract-type/ComparisonTable";
import { useFaqFlyout } from "../../about/FaqFlyout";
import { createLocationPath } from "../../shared/utils/Location";
import { useContractLoginCallbackPageLocation } from "../../contract/contract-type/ContractLoginCallbackPage";
import { useContractParamsContext } from "../../contract/ContractParams";

const FlowHeaderWrapper = styled.div`
  background-color: ${({ theme }) => theme.nafColor.signature.white};
  width: 100%;
  z-index: 2;
`;

export const ProgressBar = styled.header`
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  width: 100%;
  transform-origin: 0 50%;

  background-color: ${(props) => props.theme.nafColor.primary.kart};
`;

export const ProgressWrapper = styled.div`
  transform-origin: 50% 0;
  transition: transform 0.15s linear;
  margin: 0 auto;
  overflow-x: hidden;
  background-color: ${(props) => props.theme.nafColor.primary.dew};
  height: ${(props) => props.theme.spacing.space8};

  ${ProgressBar} {
    height: ${(props) => props.theme.spacing.space8};
  }
`;

export function NoPreviewFlowHeader() {
  const { progress } = useHeaderWithProgress();
  const params = useContractParamsContext();
  const loginCallbackLocation = useContractLoginCallbackPageLocation(params);
  const authHeaderProps = useAuthHeaderElements(
    createLocationPath(loginCallbackLocation)
  );
  const wrapper = useRef<HTMLDivElement | null>(null);
  const faqProps = useFaqLinkItem();
  const aboutProps = useAboutLinkItem();
  const elements = useMemo(
    () => combineMenuItems([...authHeaderProps, aboutProps, faqProps]),
    [authHeaderProps, aboutProps, faqProps]
  );

  return (
    <>
      <FlowHeaderWrapper ref={wrapper}>
        <Header variant={HeaderVariant.Flow} elements={elements} />
        <ProgressWrapper>
          <ProgressBar
            style={{
              transform: `scaleX(${progress})`,
            }}
          />
        </ProgressWrapper>
      </FlowHeaderWrapper>
    </>
  );
}

export default function FlowHeader() {
  const contract = useContract();
  const role = useRole();
  const { progress, showStatic } = useHeaderWithProgress();
  const params = useContractParamsContext();
  const loginCallbackLocation = useContractLoginCallbackPageLocation(params);
  const authHeaderProps = useAuthHeaderElements(
    createLocationPath(loginCallbackLocation)
  );
  const becomeMember = useFlowBecomeMemberMenuItem();
  const wrapper = useRef<HTMLDivElement | null>(null);
  const aboutProps = createAboutLinkItem();
  const [continueLaterIsOpen, setContinueLaterIsOpen] = useState(false);
  const [previewIsOpen, setPreviewIsOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const toggleUpgradeModal = useCallback(() => setUpgradeOpen((x) => !x), []);
  const toggleContinueLaterModal = useCallback(
    () => setContinueLaterIsOpen((x) => !x),
    []
  );
  const { open: openFaq } = useFaqFlyout();
  const togglePreviewModal = useCallback(() => setPreviewIsOpen((x) => !x), []);
  const currentContractType = contract.contractType?.current;

  const elements = useMemo(() => {
    const continueLaterProps: MenuListItem = {
      key: "FortsettSenere",
      text: "Fortsett senere",
      trackEvent: EVENTS.SAVE_CONTRACT_MODAL.OPEN,
      onClick: toggleContinueLaterModal,
    };

    const faqProps: MenuListItem = {
      key: "FAQ",
      text: "Vanlige spørsmål",
      trackEvent: EVENTS.HEADER.FAQ,
      onClick: () => openFaq(),
    };

    const previewButtonProps: MenuListItem = {
      key: "Forhåndsvisning",
      text: "Forhåndsvisning",
      trackEvent: EVENTS.HEADER.PREVIEW,
      icon: {
        icon: <EyeIcon className="icon" />,
      },
      onClick: togglePreviewModal,
    };

    const hasUpgraded =
      currentContractType && currentContractType >= ContractType.Standard;

    const upgradeButton: MenuListItem = {
      key: "upgrade",
      text: hasUpgraded ? (
        <>{TYPE_NAMES[currentContractType!]}</>
      ) : (
        "Oppgrader kontrakten"
      ),
      trackEvent: EVENTS.HEADER.UPGRADE_CONTRACT,
      icon: { icon: <StarIcon /> },
      onClick: toggleUpgradeModal,
    };
    return combineMenuItems([
      previewButtonProps,
      upgradeButton,
      ...authHeaderProps,
      becomeMember,
      continueLaterProps,
      aboutProps,
      faqProps,
    ]);
  }, [
    toggleUpgradeModal,
    toggleContinueLaterModal,
    openFaq,
    togglePreviewModal,
    currentContractType,
    authHeaderProps,
    becomeMember,
    aboutProps,
  ]);

  return showStatic ? (
    <StaticHeader contract={contract} role={role} />
  ) : (
    <>
      <FlowHeaderWrapper ref={wrapper}>
        <Header
          variant={HeaderVariant.Flow}
          elements={elements}
          contract={contract}
          role={role}
        />
        <ProgressWrapper>
          <ProgressBar
            style={{
              transform: `scaleX(${progress})`,
            }}
          />
        </ProgressWrapper>
        <ContinueLaterModal
          isOpen={continueLaterIsOpen}
          onClose={() => setContinueLaterIsOpen(false)}
        />
        <PreviewModal
          isOpen={previewIsOpen}
          onClose={() => setPreviewIsOpen(false)}
        />
        <UpgradeContractModal
          isOpen={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
        />
      </FlowHeaderWrapper>
    </>
  );
}
